// src/App.tsx (통째로 교체용 최종본)
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useEffect } from "react";

import Header from "./components/Header";
import MobileBottomNav from "./components/MobileBottomNav";

import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Registration from "./pages/Registration";
import FindAccount from "./pages/FindAccount";
import Pay from "./pages/Pay";
import PayNow from "./pages/PayNow";
import PaySuccess from "./pages/PaySuccess";
import PayFail from "./pages/PayFail";

import { AdminRoute } from "./components/AdminRoute";
import ProtectedRoute from "./components/ProtectedRoute";

import MyPage from "./pages/MyPage";

import RentIntro from "./pages/RentIntro";
import ReverifyID from "./pages/ReverifyID";

// 계약 등록 2단계
import ContractStart from "./pages/ContractStart";
import ContractTransfer from "./pages/ContractTransfer";

// 관리자 레이아웃/핵심
import AdminLayout from "./components/admin/AdminLayout";
import AdminUsers from "./pages/admin/Users";

// ✅ 회사정보 페이지
import AdminCompany from "./pages/admin/CompanyInfo";

import AdminPaymentRequests from "./pages/admin/PaymentRequests";
import AdminPaymentRequestView from "./pages/admin/PaymentRequestView";

// ✅ 홈 공지 팝업 이미지 설정
import HomeNoticeSettings from "./pages/admin/HomeNoticeSettings";

// ✅ 작업중 화면 설정 페이지
import AdminMainSettings from "./pages/admin/MainSettings";

// ✅ 1:1 문의 사용자 페이지 (사용자 노출 비활성화)

// 게시판 (관리자용)
import QnaCategories from "./pages/admin/QnaCategories";
import AdminGuides from "./pages/admin/Guides";
import AdminNotices from "./pages/admin/Notices";
import ServiceCategories from "./pages/admin/ServiceCategories";

// ✅ FAQ 관리(추가/수정/삭제) 관리자 페이지(신규)
import FaqAdmin from "./pages/admin/FaqAdmin";

// ✅ 가맹점(관리자 계정 리스트) 페이지
import Merchants from "./pages/admin/Merchants";

// 카드사 무이자/할부 설정 (관리자용 페이지)
import CardInstallments from "./pages/admin/CardInstallments";

// ✅ 약관/개인정보 관리자 페이지
import AdminPrivacy from "./pages/admin/PrivacyAdmin";
import AdminTerms from "./pages/admin/TermsAdmin";
import AiBlogSettings from "./pages/admin/AiBlogSettings";

// ✅ 모바일 홈 배너 설정(관리자)
import HomeBanners from "./pages/admin/HomeBanners";

// 로그인 후 “어떤 결제가 필요하세요?”
import GetStarted from "./pages/GetStarted";

// 고객용 안내/지원 페이지
import Support from "./pages/Support";
import GuideIntro from "./pages/GuideIntro";
import FaqPage from "./pages/FaqPage";

// ✅ 약관/개인정보 (사용자용)
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import NetworkDebug from "./pages/NetworkDebug";

// Auth
import { useAuth } from "./context/AuthProvider";

// 소셜 로그인 완료 후 토큰 처리용
import SocialLoginLanding from "./pages/SocialLoginLanding";

// 본인인증 온보딩 페이지 (신분증 + 1원 인증)
import Onboarding from "./pages/Onboarding";

// 1원 계좌 인증 전용 페이지
import AccountVerify from "./pages/AccountVerify";

// ✅ 모바일 전용 "앱 홈"
import MobileAppHome from "./pages/MobileAppHome";
import PaylistEntry from "./pages/PaylistEntry";

// ✅ 모바일 판별 유틸
import { isMobileLike } from "./lib/isMobile";

// ✅ 작업중 모드 플래그 훅/화면
import { useSiteFlags } from "./lib/useSiteFlags";
import MaintenanceScreen from "./components/MaintenanceScreen";

import PayEdit from "./pages/PayEdit";
import SeoMetaManager from "./components/SeoMetaManager";
import { isJulyupayApp } from "./lib/openExternal";
import "./styles/mobile-shell.css";

// 게스트 전용 보호(로그인/회원가입)
function GuestOnly({ children }: { children: ReactNode }) {
  const { ready } = useAuth();
  if (!ready) return null;
  // Auth screens own the destination so login/signup can preserve the request's next path.
  return <>{children}</>;
}

// ✅ 모바일 전용 라우트 가드
function MobileOnly({ children }: { children: ReactNode }) {
  const isApp = isJulyupayApp();
  return isMobileLike() || isApp ? <>{children}</> : <Navigate to="/" replace />;
}

export default function App() {
  const location = useLocation();
  const path = location.pathname;

  useEffect(() => {
    if (!path.startsWith("/admin") && !location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [path, location.hash]);

  useEffect(() => {
    let startY = 0;
    const onTouchStart = (e: TouchEvent) => {
      startY = e.touches?.[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      const y = e.touches?.[0]?.clientY ?? 0;
      const pullingDown = y > startY + 6;
      if (window.scrollY <= 0 && pullingDown) e.preventDefault();
    };
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  const isAdminPath = path.startsWith("/admin");
  const isPaymentEntry = ["/start", "/registration", "/login", "/signup", "/find-account", "/onboarding", "/account/verify", "/ekyc/reverify", "/pay-now"].includes(path)
    || path.startsWith("/contracts/") || /^\/pay\/[^/]+\/edit$/.test(path);
  const showBottomNav = !isAdminPath && !isPaymentEntry && !["/social-login", "/pay/success", "/pay/fail"].includes(path);

  const { user, ready } = useAuth();
  const { flags, loading: flagsLoading } = useSiteFlags();

  // user/flags 로딩 중에는 깜빡임 방지
  if (!ready || flagsLoading) return null;

  const maintenanceOn = !!flags?.maintenance_enabled;

  // ✅ 작업중이어도 반드시 열어둘 경로들(관리자 로그인/관리자영역)
  const bypassMaintenance =
    path.startsWith("/admin") ||
    path.startsWith("/login") ||
    path.startsWith("/find-account") ||
    path.startsWith("/forgot") ||
    path.startsWith("/social-login") ||
    path.startsWith("/debug/network");

  // ✅ 작업중 모드 ON: (bypass 제외) + (관리자 계정 제외)
  if (maintenanceOn && !bypassMaintenance && !user?.is_admin) {
    const msg =
      (flags?.maintenance_message || "").trim() ||
      "현재 화면이 작업 중입니다.\n잠시 후 다시 이용해 주세요.";
    return <MaintenanceScreen message={msg} />;
  }

  return (
    <div className={`jp-app-shell${isJulyupayApp() ? " jp-native-app" : ""}${isPaymentEntry ? " jp-focused-shell" : ""}${isAdminPath ? " jp-admin-shell" : ""}`}>
      <SeoMetaManager path={path} />
      {/* ✅ 헤더 고정 */}
      {!isAdminPath && <div className="fixed inset-x-0 top-0 z-50">
        <Header focused={isPaymentEntry} />
      </div>}

      <main
        className={[
          "jp-app-main",
          showBottomNav ? "jp-has-tabs" : "",
        ].join(" ")}
      >
        <Routes>
          {/* ================== 공개 ================== */}
          <Route path="/" element={<Home />} />

          {/* ✅ 모바일 진입 기본 경로 */}
          <Route path="/m" element={<Navigate to="/m/home" replace />} />
          <Route path="/m/intro" element={<Navigate to="/m/home" replace />} />

          {/* ✅ 모바일 전용 앱 홈 */}
          <Route
            path="/m/home"
            element={
              <MobileOnly>
                <MobileAppHome />
              </MobileOnly>
            }
          />

          <Route
            path="/login"
            element={
              <GuestOnly>
                <Login />
              </GuestOnly>
            }
          />
          <Route
            path="/signup"
            element={
              <GuestOnly>
                <Signup />
              </GuestOnly>
            }
          />

          {/* 아이디/비밀번호 찾기 (공개) */}
          <Route path="/find-account" element={<FindAccount />} />
          <Route path="/forgot" element={<Navigate to="/find-account" replace />} />

          {/* 소셜 로그인 토큰 처리 페이지 (공개) */}
          <Route path="/social-login" element={<SocialLoginLanding />} />

          {/* 계약 등록 진입: ✅ 첫 선택 페이지 스킵하고 바로 계약등록으로 */}
          <Route
            path="/registration"
            element={<Registration />}
          />

          {/* 고객센터 / 안내 페이지 (공개) */}
          <Route path="/support" element={<Support />} />
          <Route path="/guide" element={<GuideIntro />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/qna" element={<Navigate to="/support" replace />} />

          {/* ✅ 약관/개인정보 (공개) */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/debug/network" element={<NetworkDebug />} />

          {/* ================== 로그인 필요 구간 ================== */}
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

          <Route
            path="/account/verify"
            element={
              <ProtectedRoute>
                <AccountVerify />
              </ProtectedRoute>
            }
          />

          <Route
            path="/start"
            element={<Navigate to={`/registration${location.search}`} replace />}
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Navigate to="/mypage" replace />
              </ProtectedRoute>
            }
          />

          <Route
            path="/mypage"
            element={
              <ProtectedRoute>
                <MyPage />
              </ProtectedRoute>
            }
          />
          <Route path="/my" element={<Navigate to="/mypage" replace />} />

          <Route
            path="/mypage/inquiries"
            element={
              <ProtectedRoute>
                <Navigate to="/support" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mypage/inquiries/:id"
            element={
              <ProtectedRoute>
                <Navigate to="/support" replace />
              </ProtectedRoute>
            }
          />

          <Route
            path="/pay"
            element={
              <ProtectedRoute>
                <Pay />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pay/:id/edit"
            element={
              <ProtectedRoute>
                <PayEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pay-now"
            element={
              <ProtectedRoute>
                <PayNow />
              </ProtectedRoute>
            }
          />

          {/* 결제 결과(공개) */}
          <Route path="/pay/success" element={<PaySuccess />} />
          <Route path="/pay/fail" element={<PayFail />} />

          {/* 계약 등록 */}
          <Route
            path="/contracts/new"
            element={
              <ProtectedRoute>
                <ContractStart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contracts/:id/transfer"
            element={
              <ProtectedRoute>
                <ContractTransfer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contracts/:id"
            element={
              <ProtectedRoute>
                <ContractTransfer />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ekyc/reverify"
            element={
              <ProtectedRoute>
                <ReverifyID />
              </ProtectedRoute>
            }
          />

          {/* ================== 관리자 ================== */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<Navigate to="payments" replace />} />

            {/* 결제요청서 */}
            <Route path="payments" element={<AdminPaymentRequests />} />
            <Route path="payments/:id" element={<AdminPaymentRequestView />} />

            {/* 이전 경로 호환 */}
            <Route path="payment-requests" element={<AdminPaymentRequests />} />
            <Route path="payment-requests/:id" element={<AdminPaymentRequestView />} />

            {/* 기본 관리자 메뉴 */}
            <Route path="users" element={<AdminUsers />} />

            {/* 회사정보 */}
            <Route path="company" element={<AdminCompany />} />

            {/* 상담은 카카오톡으로 통일하고 기존 문의 주소는 관리 홈으로 연결합니다. */}
            <Route path="inquiries" element={<Navigate to="/admin/payments" replace />} />
            <Route path="inquiries/:id" element={<Navigate to="/admin/payments" replace />} />

            {/* 게시판/설정 */}
            <Route path="boards/qna-categories" element={<QnaCategories />} />

            {/* ✅ FAQ 관리: 기존 경로 유지하면서 새 CRUD 화면 연결 */}
            <Route path="boards/faq" element={<FaqAdmin />} />

            <Route path="boards/guides" element={<AdminGuides />} />
            <Route path="boards/notices" element={<AdminNotices />} />

            {/* 가맹점 */}
            <Route path="merchants" element={<Merchants />} />

            {/* 첨부서류 안내 설정 */}
            <Route
              path="settings/service-categories"
              element={<ServiceCategories />}
            />

            {/* 홈 공지 팝업 이미지 설정 */}
            <Route path="settings/home-notice" element={<HomeNoticeSettings />} />

            {/* 모바일 홈 배너(이미지) 설정 */}
            <Route path="settings/banners" element={<HomeBanners />} />

            {/* 카드 무이자/할부 */}
            <Route path="settings/payments" element={<CardInstallments />} />
            <Route path="settings/installments" element={<CardInstallments />} />
            <Route path="settings" element={<Navigate to="settings/payments" replace />} />

            {/* 화면 작업 중 설정 */}
            <Route path="settings/main" element={<AdminMainSettings />} />
            <Route path="settings/ai-blog" element={<AiBlogSettings />} />

            {/* 약관/개인정보 관리 */}
            <Route path="settings/privacy" element={<AdminPrivacy />} />
            <Route path="settings/terms" element={<AdminTerms />} />
          </Route>

          {/* ================== 레거시/알림톡 버튼 링크 호환 ================== */}
          {/* BizM 템플릿 버튼 진입: 모바일은 앱 딥링크 우선 시도 후 /pay fallback */}
          <Route path="/payment/list" element={<PaylistEntry />} />
          <Route path="/payment" element={<Navigate to="/pay" replace />} />
          <Route path="/payment/*" element={<Navigate to="/pay" replace />} />

          {/* 기존 레거시 리다이렉트 */}
          <Route path="/docs" element={<Navigate to="/contracts/new" replace />} />
          <Route path="/docs/upload" element={<Navigate to="/contracts/new" replace />} />
          <Route path="/uploads" element={<Navigate to="/contracts/new" replace />} />
          <Route path="/pay/rent" element={<Navigate to="/registration?type=rent" replace />} />
          <Route
            path="/contract/add-contract"
            element={<Navigate to="/contracts/new" replace />}
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* ✅ 바텀탭 고정 */}
      {showBottomNav && <MobileBottomNav />}
    </div>
  );
}
